import { useDocumentTitleDetail } from "@/lib/documentTitle/useDocumentTitleDetail";
import {
  TabButton as UiTabButton,
  TextButton,
} from "@/components/ui/pressable";
import { useState, type ReactNode } from "react";
import { useFadeOnChange } from "@/hooks/useFadeOnChange";
import { useSearchParams, Link } from "react-router";
import { useGetIdentity, useGetList } from "ra-core";
import type { OrgModulesState } from "@/modules/registry";
import { formatDistanceToNow } from "date-fns";
import { ChevronDown, ExternalLink, Home, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar } from "@/components/atomic-crm/contacts/Avatar";
import { CompanyAvatar } from "@/components/atomic-crm/companies/CompanyAvatar";
import type { Company, Contact } from "@/components/atomic-crm/types";
import { getClientShowPath, getContactShowPath } from "@/app/routing";
import { cn } from "@/lib/utils";
import { ClientActivityTimeline } from "@/modules/clients/ClientActivityTimeline";
import { ClientThreadsTab } from "@/modules/threads/ClientThreadsTab";
import { RecordTabNavContext } from "@/modules/clients/record/recordTabNav";
import {
  buildClientTimelineFilter,
  type ClientTimelineEvent,
} from "@/modules/clients/clientTimelineEvents";
import { ClientFinancialTab } from "@/modules/clients/ClientFinancialTab";
import {
  ClientFilesTab,
  ClientProjectsTab,
  ClientTasksTab,
  ClientTicketsTab,
} from "@/modules/clients/ClientTabPanels";
import {
  companyScope,
  contactScope,
  type ClientScope,
} from "@/modules/clients/clientScope";
import { formatTabCount } from "@/modules/clients/clientShowUtils";
import { useClientTabCounts } from "@/modules/clients/useClientTabCounts";
import { useContactTabCounts } from "@/modules/contacts/useContactTabCounts";
import { isLeadLifecycleStatus } from "@/modules/constants/contactStatus";
import { LeadPipelinePanel } from "@/modules/leads/LeadPipelinePanel";
import { ContactCalendarEventsSection } from "@/modules/calendar/ProjectCalendarEventsList";
import {
  CompanyRecordFields,
  ContactRecordFields,
} from "@/modules/clients/record/RecordFields";
import {
  CompanyIdentity,
  CompanyRelatedSections,
  ContactIdentity,
  ContactRelatedSections,
} from "@/modules/clients/record/RecordIdentity";
import {
  DEFAULT_PROFILE_TAB,
  PROFILE_TAB_LABELS,
  resolveProfileTab,
  type ProfileTab,
  isProfileTabEnabled,
} from "@/modules/clients/record/profileTabs";

export type RecordViewMode = "page" | "panel";
type PanelTab = ProfileTab | "home";

type TabDef = { id: ProfileTab; count?: number; content: ReactNode };

/** Page: tab lives in ?tab= (old links still resolve). Panel: local state. */
const useRecordTab = (
  mode: RecordViewMode,
  available: readonly ProfileTab[],
  fallback: ProfileTab,
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [panelTab, setPanelTab] = useState<PanelTab>("home");
  if (mode === "panel") {
    const current: PanelTab =
      panelTab === "home" || available.includes(panelTab) ? panelTab : "home";
    return { current, setTab: (tab: PanelTab) => setPanelTab(tab) };
  }
  const current = resolveProfileTab(
    searchParams.get("tab"),
    searchParams.get("section"),
    available,
    fallback,
  );
  const setTab = (tab: PanelTab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === fallback || tab === "home") next.delete("tab");
    else next.set("tab", tab);
    next.delete("section");
    next.delete("stage");
    setSearchParams(next, { replace: true });
  };
  return { current: current as PanelTab, setTab };
};

const tabLabel = (tab: TabDef) =>
  `${PROFILE_TAB_LABELS[tab.id]}${formatTabCount(tab.count)}`;

const TabButton = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) => (
  <UiTabButton variant="underline" selected={active} onClick={onClick}>
    {children}
  </UiTabButton>
);

const timelineContent = (
  scope: ClientScope,
  primaryContactId?: Contact["id"] | null,
) => (
  <ClientActivityTimeline
    scope={scope}
    primaryContactId={primaryContactId}
    showActions
  />
);

/** Meetings and reminders of the people in view, upcoming then past. */
const calendarContent = (contactIds: Contact["id"][]) => (
  <div className="space-y-4">
    <ContactCalendarEventsSection
      contactIds={contactIds}
      title="Upcoming"
      compact
    />
    <ContactCalendarEventsSection
      contactIds={contactIds}
      title="Past"
      showCompleted
      compact
    />
  </div>
);

/** Team threads of the people and their projects (with @mentions). */
const notesContent = (
  scope: ClientScope,
  contactIds: Contact["id"][],
  primaryContactId?: Contact["id"] | null,
) => (
  <ClientThreadsTab
    scope={scope}
    contactIds={contactIds}
    primaryContactId={primaryContactId}
  />
);

/** Shared frame: page = fields column + tab bar; panel = Home/Timeline/Tasks/More. */
const RecordFrame = ({
  mode,
  tabs: allTabs,
  fallback,
  identity,
  fields,
  related,
  panelHeader,
  openPath,
  onClose,
}: {
  mode: RecordViewMode;
  tabs: TabDef[];
  fallback: ProfileTab;
  /** Receives a tab opener (stats link to Tasks). */
  identity: (openTab: (tab: ProfileTab) => void) => ReactNode;
  fields: ReactNode;
  related: ReactNode;
  panelHeader: ReactNode;
  openPath: string;
  onClose?: () => void;
}) => {
  // Only tabs of modules the company has on (Apps).
  const { data: member } = useGetIdentity();
  const orgModules = (member as { org_modules?: OrgModulesState | null })
    ?.org_modules;
  const tabs = allTabs.filter((tab) => isProfileTabEnabled(orgModules, tab.id));
  const available = tabs.map((tab) => tab.id);
  // The default tab may belong to a module that is off (e.g. Pipeline).
  const effectiveFallback = available.includes(fallback)
    ? fallback
    : (available[0] ?? fallback);
  const { current, setTab } = useRecordTab(mode, available, effectiveFallback);
  const active = tabs.find((tab) => tab.id === current);

  return (
    <RecordTabNavContext.Provider value={{ mode, openTab: setTab }}>
      <RecordFrameLayout
        mode={mode}
        tabs={tabs}
        current={current}
        setTab={setTab}
        active={active}
        identity={identity}
        fields={fields}
        related={related}
        panelHeader={panelHeader}
        openPath={openPath}
        onClose={onClose}
      />
    </RecordTabNavContext.Provider>
  );
};

const RecordFrameLayout = ({
  mode,
  tabs,
  current,
  setTab,
  active,
  identity,
  fields,
  related,
  panelHeader,
  openPath,
  onClose,
}: {
  mode: RecordViewMode;
  tabs: TabDef[];
  current: ProfileTab | "home";
  setTab: (tab: ProfileTab | "home") => void;
  active?: TabDef;
  identity: (openTab: (tab: ProfileTab) => void) => ReactNode;
  fields: ReactNode;
  related: ReactNode;
  panelHeader: ReactNode;
  openPath: string;
  onClose?: () => void;
}) => {
  // Fade the tab content in when the tab changes.
  const contentRef = useFadeOnChange<HTMLDivElement>(current);
  if (mode === "panel") {
    const visible = tabs.filter((tab) =>
      ["pipeline", "timeline", "tasks"].includes(tab.id),
    );
    const more = tabs.filter((tab) => !visible.includes(tab));
    const moreActive = more.find((tab) => tab.id === current);
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex shrink-0 items-center gap-2 border-b px-3 py-2.5">
          {onClose ? (
            <IconButton
              className="size-8"
              aria-label="Close preview"
              onClick={onClose}
            >
              <X className="size-4" />
            </IconButton>
          ) : null}
          <div className="min-w-0 flex-1">{panelHeader}</div>
          <Button size="sm" className="h-8 shrink-0 gap-1.5" asChild>
            <Link to={openPath}>
              <ExternalLink className="size-3.5" />
              Open
            </Link>
          </Button>
        </div>
        <div
          className="flex shrink-0 items-center gap-1 overflow-x-auto border-b px-2"
          role="tablist"
        >
          <TabButton active={current === "home"} onClick={() => setTab("home")}>
            <Home className="size-3.5" />
            Home
          </TabButton>
          {visible.map((tab) => (
            <TabButton
              key={tab.id}
              active={current === tab.id}
              onClick={() => setTab(tab.id)}
            >
              {tabLabel(tab)}
            </TabButton>
          ))}
          {more.length ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <UiTabButton variant="underline" selected={Boolean(moreActive)}>
                  {moreActive ? tabLabel(moreActive) : `+${more.length} More`}
                  <ChevronDown className="size-3.5" />
                </UiTabButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {more.map((tab) => (
                  <DropdownMenuItem key={tab.id} onClick={() => setTab(tab.id)}>
                    {tabLabel(tab)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
        <div
          ref={contentRef}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
        >
          {current === "home" ? (
            <>
              {identity(setTab)}
              <div className="border-t pt-1 pb-2">{fields}</div>
              {related}
            </>
          ) : (
            <div className="h-full p-4">{active?.content}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    // Two separate cards, 16px apart. On desktop the page fills the
    // viewport and each card scrolls on its own.
    <div className="flex flex-col gap-4 lg:h-full lg:min-h-0 lg:flex-row">
      <aside className="min-w-0 overflow-hidden rounded-xl border bg-card lg:w-[22rem] lg:shrink-0 lg:overflow-y-auto lg:overscroll-contain">
        {identity(setTab)}
        <div className="border-t pt-1 pb-2">{fields}</div>
        {related}
      </aside>
      <section className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border bg-card lg:min-h-0">
        <div
          className="flex shrink-0 items-center gap-1 overflow-x-auto border-b px-3"
          role="tablist"
        >
          {tabs.map((tab) => (
            <TabButton
              key={tab.id}
              active={current === tab.id}
              onClick={() => setTab(tab.id)}
            >
              {tabLabel(tab)}
            </TabButton>
          ))}
        </div>
        <div
          ref={contentRef}
          className="p-4 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-contain"
        >
          {active?.content}
        </div>
      </section>
    </div>
  );
};

/** Last activity (newest timeline event) and open/overdue tasks. */
const RecordStats = ({
  scope,
  openTasks,
  overdueTasks,
  onOpenTasks,
}: {
  scope: ClientScope;
  openTasks: number;
  overdueTasks: number;
  onOpenTasks: () => void;
}) => {
  const { data: latest = [] } = useGetList<ClientTimelineEvent>(
    "client_activity_timeline",
    {
      filter: buildClientTimelineFilter(scope, "all"),
      sort: { field: "occurred_at", order: "DESC" },
      pagination: { page: 1, perPage: 1 },
    },
    { staleTime: 30_000 },
  );
  const last = latest[0]?.occurred_at;
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
      <span>
        Last activity{" "}
        {last ? formatDistanceToNow(new Date(last), { addSuffix: true }) : "—"}
      </span>
      <TextButton
        tone="destructive"
        size="inherit"
        className={cn(overdueTasks > 0 && "")}
        onClick={onOpenTasks}
      >
        {overdueTasks > 0
          ? `${overdueTasks} overdue · ${openTasks} open tasks`
          : `${openTasks} open task${openTasks === 1 ? "" : "s"}`}
      </TextButton>
    </div>
  );
};

const createdAgo = (value?: string | null) =>
  value
    ? `Created ${formatDistanceToNow(new Date(value), { addSuffix: true })}`
    : null;

const PanelTitle = ({
  avatar,
  title,
  subtitle,
}: {
  avatar: ReactNode;
  title: string;
  subtitle?: string | null;
}) => (
  <div className="flex min-w-0 items-center gap-2">
    {avatar}
    <span className="truncate font-semibold">{title}</span>
    {subtitle ? (
      <span className="truncate text-sm text-muted-foreground">{subtitle}</span>
    ) : null}
  </div>
);

/** Account record: fields and people on the left, everything else in tabs. */
export const CompanyRecordView = ({
  record,
  mode,
  onClose,
  onAddContact,
  onOpenContact,
}: {
  record: Company;
  mode: RecordViewMode;
  onClose?: () => void;
  onAddContact?: () => void;
  onOpenContact?: (contact: Contact) => void;
}) => {
  const counts = useClientTabCounts(record.id);
  useDocumentTitleDetail(record.name);
  const scope = companyScope(record.id);
  const syncUrl = mode === "page";
  const tabs: TabDef[] = [
    {
      id: "timeline",
      content: timelineContent(scope, record.primary_contact_id),
    },
    {
      id: "tasks",
      count: counts.tasks,
      content: (
        <ClientTasksTab
          contactIds={counts.contactIds}
          primaryContactId={record.primary_contact_id}
        />
      ),
    },
    {
      id: "notes",
      count: counts.notes,
      content: notesContent(
        scope,
        counts.contactIds,
        record.primary_contact_id,
      ),
    },
    {
      id: "emails",
      content: (
        <ClientActivityTimeline
          scope={scope}
          initialFilter="emails"
          hideFilters
        />
      ),
    },
    {
      id: "calendar",
      content: calendarContent(counts.contactIds),
    },
    {
      id: "deals",
      count: counts.projects,
      content: <ClientProjectsTab scope={scope} />,
    },
    {
      id: "financial",
      count:
        counts.invoices + counts.proposals + counts.contracts + counts.payments,
      content: (
        <ClientFinancialTab
          scope={scope}
          syncUrl={syncUrl}
          counts={{
            invoices: counts.invoices,
            proposals: counts.proposals,
            contracts: counts.contracts,
            payments: counts.payments,
          }}
        />
      ),
    },
    {
      id: "tickets",
      count: counts.tickets,
      content: <ClientTicketsTab companyId={record.id} />,
    },
    { id: "files", content: <ClientFilesTab companyId={record.id} /> },
  ];

  return (
    <RecordFrame
      mode={mode}
      tabs={tabs}
      fallback={DEFAULT_PROFILE_TAB}
      identity={(openTab) => (
        <CompanyIdentity
          record={record}
          stats={
            <RecordStats
              scope={scope}
              openTasks={counts.tasks}
              overdueTasks={counts.overdueTasks}
              onOpenTasks={() => openTab("tasks")}
            />
          }
        />
      )}
      fields={<CompanyRecordFields record={record} />}
      related={
        <CompanyRelatedSections
          record={record}
          onAddContact={onAddContact}
          onOpenContact={onOpenContact}
        />
      }
      panelHeader={
        <PanelTitle
          avatar={<CompanyAvatar record={record} width={20} height={20} />}
          title={record.name || "Untitled"}
          subtitle={createdAgo(record.created_at)}
        />
      }
      openPath={getClientShowPath(record.id)}
      onClose={onClose}
    />
  );
};

/** Person record: fields and account on the left, their activity in tabs. */
export const ContactRecordView = ({
  record,
  mode,
  onClose,
}: {
  record: Contact;
  mode: RecordViewMode;
  onClose?: () => void;
}) => {
  const counts = useContactTabCounts(record);
  useDocumentTitleDetail(
    [record.first_name, record.last_name].filter(Boolean).join(" "),
  );
  const scope = contactScope(record.id, record.company_id);
  const isLead = isLeadLifecycleStatus(record.status);
  const syncUrl = mode === "page";
  const tabs: TabDef[] = [
    ...(isLead
      ? [
          {
            id: "pipeline" as const,
            content: <LeadPipelinePanel lead={record} embedded />,
          },
        ]
      : []),
    { id: "timeline", content: timelineContent(scope, record.id) },
    {
      id: "tasks",
      count: counts.tasks,
      content: (
        <ClientTasksTab
          contactIds={counts.contactIds}
          primaryContactId={record.id}
        />
      ),
    },
    {
      id: "notes",
      count: counts.notes,
      content: notesContent(scope, counts.contactIds, record.id),
    },
    {
      id: "emails",
      content: (
        <ClientActivityTimeline
          scope={scope}
          initialFilter="emails"
          hideFilters
        />
      ),
    },
    {
      id: "calendar",
      content: calendarContent(counts.contactIds),
    },
    {
      id: "deals",
      count: counts.projects,
      content: <ClientProjectsTab scope={scope} />,
    },
    {
      id: "financial",
      count:
        counts.invoices + counts.proposals + counts.contracts + counts.payments,
      content: (
        <ClientFinancialTab
          scope={scope}
          syncUrl={syncUrl}
          counts={{
            invoices: counts.invoices,
            proposals: counts.proposals,
            contracts: counts.contracts,
            payments: counts.payments,
          }}
        />
      ),
    },
    {
      id: "tickets",
      count: counts.tickets,
      content: (
        <ClientTicketsTab
          contactId={record.id}
          companyId={counts.hasCompany ? counts.companyId : undefined}
          scope="contact"
        />
      ),
    },
  ];
  const name =
    [record.first_name, record.last_name].filter(Boolean).join(" ") ||
    "Unnamed";

  return (
    <RecordFrame
      mode={mode}
      tabs={tabs}
      fallback={isLead ? "pipeline" : DEFAULT_PROFILE_TAB}
      identity={(openTab) => (
        <ContactIdentity
          record={record}
          stats={
            <RecordStats
              scope={scope}
              openTasks={counts.tasks}
              overdueTasks={counts.overdueTasks}
              onOpenTasks={() => openTab("tasks")}
            />
          }
        />
      )}
      fields={<ContactRecordFields record={record} />}
      related={<ContactRelatedSections record={record} />}
      panelHeader={
        <PanelTitle
          avatar={<Avatar record={record} width={20} height={20} />}
          title={name}
          subtitle={createdAgo(record.first_seen)}
        />
      }
      openPath={getContactShowPath(record.id)}
      onClose={onClose}
    />
  );
};
